import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { auth } from '@/shared/lib/firebase';
import { env } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';

/**
 * The admin's GraphQL client — the only transport the admin has; the axios
 * client it once sat beside is gone with the last REST route.
 *
 * The admin is a client-only Next app, so unlike the site there is no
 * server-component client to build: one browser instance is enough.
 */

/**
 * Attaches the Firebase ID token to every request.
 * `getIdToken()` refreshes itself when the token is within five minutes of
 * expiry, so there is no separate refresh path to mirror here.
 */
const authLink = new SetContextLink(async (prevContext) => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return {};

    // The setter's return value is shallow-merged into the context, so
    // `headers` has to be spread explicitly or the existing ones are dropped.
    return { headers: { ...prevContext.headers, Authorization: `Bearer ${token}` } };
  } catch (error) {
    logger.error('Failed to get auth token for GraphQL request', error as Error);
    return {};
  }
});

// A deployed console calls the API cross-origin, so its hostname has to be in
// ALLOWED_ORIGINS there. The dev server proxies /graphql instead (vite.config.ts),
// which is what lets a laptop talk to staging without being listed.
const httpLink = new HttpLink({
  uri: import.meta.env.DEV ? '/graphql' : `${env.VITE_API_URL}/graphql`,
});

export const apolloClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});
